import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HiMenu, HiX } from 'react-icons/hi'
import { useScrollToSection } from '@/hooks/useScrollToSection'
import { Button } from '@/components/Button'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { useLanguage } from '@/i18n/LanguageContext'

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const scrollTo = useScrollToSection()
  const { t } = useLanguage()

  const navItems = [
    { id: 'inicio', label: t.header.nav.inicio },
    { id: 'sobre', label: t.header.nav.sobre },
    { id: 'servicos', label: t.header.nav.servicos },
    { id: 'diferenciais', label: t.header.nav.diferenciais },
    { id: 'tecnologias', label: t.header.nav.tecnologias },
    { id: 'contato', label: t.header.nav.contato },
  ]

  const handleNavClick = (sectionId: string) => {
    scrollTo(sectionId)
    setMobileOpen(false)
  }

  return (
    <motion.header
      className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-200/80"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <nav className="container-custom flex items-center justify-between h-16 md:h-20" aria-label={t.header.navAriaLabel}>
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault()
            handleNavClick('inicio')
          }}
          className="inline-flex items-center transition-opacity hover:opacity-90"
          aria-label={t.header.homeAriaLabel}
        >
          <img
            src="/logo.png"
            alt="EnvProcess"
            // className="h-10 md:h-11 w-auto object-contain"
            className="w-[110px] md:w-[122px] h-auto object-contain"
          />
        </a>

        <ul className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => handleNavClick(item.id)}
                className="text-slate-600 hover:text-primary-600 font-medium transition-colors"
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          <Button onClick={() => handleNavClick('contato')}>{t.common.contactUs}</Button>
        </div>

        <button
          type="button"
          className="md:hidden p-2 text-slate-700 hover:text-primary-600 rounded-lg"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? t.header.closeMenu : t.header.openMenu}
        >
          {mobileOpen ? <HiX className="h-6 w-6" /> : <HiMenu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-slate-200 shadow-lg"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ul className="container-custom py-4 flex flex-col gap-2">
              {navItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavClick(item.id)}
                    className="block w-full text-left py-3 px-4 text-slate-700 hover:bg-slate-50 rounded-lg font-medium"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
              <li className="pt-2">
                <LanguageSwitcher className="mb-3" fullWidth />
                <Button
                  className="w-full justify-center"
                  onClick={() => handleNavClick('contato')}
                >
                  {t.common.contactUs}
                </Button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}
